import { Stack, Text } from '@mantine/core'
import { DateTimePicker } from '@mantine/dates'
import { useDisclosure } from '@mantine/hooks'
import { formatDistance } from 'date-fns'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ErrorMessage from '@/components/ErrorMessage'
import { ConfirmationLoading, ConfirmationReady } from '@/components/Identity/ConfirmModals/utils'
import { ELECTIONS_ADDRESS } from '@/constants/blockchain'
import OMMAElection from '@/contracts/OMMAElection'
import { ContractCall, useChainFunctions } from '@/hooks/useChain'
import { l1NativeWagmiConfig } from '@/plugins/auth/config'

import RootButton from '../../Buttons/RootButton'
import ModalSkeleton from '../ModalSkeleton'

interface StartElectionProps {
  close: () => void
}

const StartElection: React.FC<StartElectionProps> = ({ close }) => {
  const { t } = useTranslation()
  const { writeContract } = useChainFunctions(l1NativeWagmiConfig)

  // modal
  const [openedConfirmation, { open: openConfirmation, close: closeConfirmation }] = useDisclosure()

  // times
  const now = Date.now()
  const defaultStart = new Date(now + 3600 * 1000) // 1 hour from now
  // Round to next hour
  defaultStart.setHours(defaultStart.getHours() + 1)
  defaultStart.setMinutes(0)
  const defaultCampaign = new Date(defaultStart.getTime() + 7 * 24 * 3600 * 1000) // 1 week from start
  const defaultVoting = new Date(defaultCampaign.getTime() + 7 * 24 * 3600 * 1000) // 2 week from campaign
  const defaultEnd = new Date(defaultVoting.getTime() + 7 * 24 * 3600 * 1000) // 2 week from voting

  const [startTime, setStartTime] = useState<Date>(defaultStart)
  const [campaignTime, setCampaignTime] = useState<Date>(defaultCampaign)
  const [votingTime, setVotingTime] = useState<Date>(defaultVoting)
  const [endTime, setEndTime] = useState<Date>(defaultEnd)
  const [timeError, setTimeError] = useState<string>('')

  // Calculate duration between times
  const duration = formatDistance(endTime, startTime)

  // ready confirmation
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [isReady, setIsReady] = useState<boolean>(false)

  const contractCalls: ContractCall[] | undefined = useMemo(() => {
    const startTimestamp = Math.floor(startTime.getTime() / 1000)
    const campaignTimestamp = Math.floor(campaignTime.getTime() / 1000)
    const votingTimestamp = Math.floor(votingTime.getTime() / 1000)
    const endTimestamp = Math.floor(endTime.getTime() / 1000)

    return [
      {
        args: {
          address: ELECTIONS_ADDRESS,
          abi: OMMAElection,
          functionName: 'createElection',
          args: [startTimestamp, campaignTimestamp, votingTimestamp, endTimestamp],
        },
      },
    ]
  }, [startTime, campaignTime, votingTime, endTime])

  const checkInputs = (start: Date, campaign: Date, voting: Date, end: Date) => {
    // Validate times
    if (campaign <= start) {
      setTimeError('Campaign time must be after start time')
      return
    }
    if (voting <= campaign) {
      setTimeError('Voting time must be after campaign time')
      return
    }
    if (end <= voting) {
      setTimeError('End time must be after voting time')
      return
    }
    setTimeError('')
  }

  const handleStartElection = useCallback(async () => {
    if (!(contractCalls && contractCalls.length > 0)) return
    try {
      setIsLoading(true)
      await writeContract(contractCalls[0].args)
      setIsLoading(false)
      setIsReady(true)
    } catch (err: any) {
      console.error(err)
      setIsLoading(false)
      setError(err.message)
    }
  }, [contractCalls, writeContract])

  const handleCloseConfirmationCancel = useCallback(() => {
    setError('')
    closeConfirmation()
  }, [closeConfirmation])

  const handleCloseConfirmation = useCallback(() => {
    setError('')
    closeConfirmation()
    close()
  }, [closeConfirmation, close])

  // Check inputs when change
  useEffect(() => {
    checkInputs(startTime, campaignTime, votingTime, endTime)
  }, [startTime, campaignTime, votingTime, endTime])

  return (
    <>
      <Stack>
        <Stack>
          <DateTimePicker
            label="Start Time"
            value={startTime}
            onChange={date => date && setStartTime(date)}
            clearable={false}
            withAsterisk
            valueFormat="DD MMM YYYY, HH:mm"
          />
          <DateTimePicker
            label="Campaign Time"
            value={campaignTime}
            onChange={date => date && setCampaignTime(date)}
            clearable={false}
            withAsterisk
            valueFormat="DD MMM YYYY, HH:mm"
          />
          <DateTimePicker
            label="Voting Time"
            value={votingTime}
            onChange={date => date && setVotingTime(date)}
            clearable={false}
            withAsterisk
            valueFormat="DD MMM YYYY, HH:mm"
          />
          <DateTimePicker
            label="End Time"
            value={endTime}
            onChange={date => date && setEndTime(date)}
            clearable={false}
            withAsterisk
            valueFormat="DD MMM YYYY, HH:mm"
          />
          <Text size="sm">Duration: {duration}</Text>
          {timeError && <Text size="sm">{timeError}</Text>}
        </Stack>

        <Stack style={{ flexDirection: 'row', width: '100%' }}>
          <RootButton style1={false} secondary expand onClick={close}>
            {t('components.governance.startElection.close', 'Close')}
          </RootButton>
          <RootButton style1={false} expand onClick={openConfirmation} disabled={timeError !== ''}>
            {t('components.governance.startElection.button', 'Start Election')}
          </RootButton>
        </Stack>
      </Stack>
      <ModalSkeleton
        opened={openedConfirmation}
        onClose={
          isReady && !isLoading && !error ? handleCloseConfirmation : handleCloseConfirmationCancel
        }
        title="Confirmation"
      >
        {error && (
          <ErrorMessage
            message={t(
              'components.governance.modal.startElection.errorMessage',
              'Error while starting election'
            )}
            details={error}
          />
        )}
        {isLoading && (
          <ConfirmationLoading
            title={t(
              'components.governance.modal.startElection.loadingMessage',
              'Validating Election Start'
            )}
          />
        )}
        {isReady && !isLoading && !error && (
          <ConfirmationReady
            message={t(
              'components.governance.modal.startElection.readyMessage',
              'Election Started'
            )}
            success={handleCloseConfirmation}
          />
        )}
        {!isReady && !error && !isLoading && (
          <>
            <Text>
              {t(
                'components.governance.modal.startElection.confirmMessage',
                'You are about to Start an Election. This action is irreversible.'
              )}
            </Text>
            <Stack style={{ flexDirection: 'row', marginTop: '20px' }}>
              <RootButton onClick={handleCloseConfirmationCancel} secondary style1={false} expand>
                {t('buttons.cancel', 'Cancel')}
              </RootButton>
              <RootButton
                disabled={timeError !== ''}
                onClick={handleStartElection}
                style1={false}
                expand
              >
                {t('buttons.confirm', 'Confirm')}
              </RootButton>
            </Stack>
          </>
        )}
      </ModalSkeleton>
    </>
  )
}

export default StartElection
